import { TonConnectUI, SendTransactionRequest } from "@tonconnect/ui";
import { toUserFriendlyAddress } from "@tonconnect/sdk";

const MANIFEST_URL = "https://state-io-prod.t3on.site/tonconnect-manifest.json";

class TonConnectManager {
  private ui: TonConnectUI | null = null;
  private connectionPromise: Promise<void> | null = null;
  private connectionReject: ((err: Error) => void) | null = null;

  init(): void {
    if (this.ui) return;
    this.ui = new TonConnectUI({
      manifestUrl: MANIFEST_URL,
    });
  }

  isConnected(): boolean {
    return this.ui?.connected ?? false;
  }

  async connect(): Promise<void> {
    this.init();
    if (this.ui!.connected) return;

    // Prevent multiple concurrent connection attempts
    if (this.connectionPromise) return this.connectionPromise;

    this.connectionPromise = new Promise<void>((resolve, reject) => {
      let settled = false;
      this.connectionReject = reject;

      const unsub = this.ui!.onStatusChange((wallet) => {
        if (settled) return;
        settled = true;
        unsub();
        this.connectionPromise = null;
        this.connectionReject = null;
        if (wallet) {
          resolve();
        } else {
          reject(new Error("Wallet connection cancelled"));
        }
      });

      // Open modal — if user closes it, onStatusChange fires with null
      this.ui!.openModal().catch((err: any) => {
        if (settled) return;
        settled = true;
        unsub();
        this.connectionPromise = null;
        reject(err);
      });

      // Timeout: if nothing happens in 2 minutes, reject
      setTimeout(() => {
        if (settled) return;
        settled = true;
        unsub();
        this.connectionPromise = null;
        try { this.ui!.closeModal(); } catch {}
        reject(new Error("Wallet connection timeout"));
      }, 120_000);
    });

    return this.connectionPromise;
  }

  async sendTransaction(to: string, amountNano: string, memo: string): Promise<void> {
    this.init();
    if (!this.ui!.connected) {
      await this.connect();
    }

    const payload = this.buildCommentPayload(memo);

    const tx: SendTransactionRequest = {
      validUntil: Math.floor(Date.now() / 1000) + 600, // 10 min
      messages: [
        {
          address: to,
          amount: amountNano,
          payload,
        },
      ],
    };

    try {
      await this.ui!.sendTransaction(tx);
    } finally {
      try { this.ui!.closeModal(); } catch {}
    }
  }

  onStatusChange(cb: (wallet: any) => void): () => void {
    this.init();
    return this.ui!.onStatusChange(cb);
  }

  getAddress(): string | null {
    if (!this.ui?.connected) return null;
    const raw = this.ui.wallet?.account?.address ?? null;
    if (!raw) return null;
    try {
      return toUserFriendlyAddress(raw);
    } catch {
      return raw;
    }
  }

  async getBalance(): Promise<string | null> {
    const addr = this.getAddress();
    if (!addr) return null;
    try {
      const res = await fetch(`https://toncenter.com/api/v2/getAddressBalance?address=${addr}`);
      const data = await res.json();
      if (data.ok && data.result) {
        const nano = BigInt(data.result);
        return (Number(nano) / 1e9).toFixed(2);
      }
    } catch {}
    return null;
  }

  async disconnect(): Promise<void> {
    if (!this.ui) return;
    await this.ui.disconnect();
  }

  /** Reset state after error — allows retry */
  reset(): void {
    if (this.connectionReject) {
      this.connectionReject(new Error("Reset"));
      this.connectionReject = null;
    }
    this.connectionPromise = null;
    try { this.ui?.closeModal(); } catch {}
  }

  /** Encode text comment as TON Cell BOC (base64) */
  private buildCommentPayload(text: string): string {
    const encoder = new TextEncoder();
    const textBytes = encoder.encode(text);

    // Comment cell data: 32-bit zero opcode + UTF-8 text
    const cellData = new Uint8Array(4 + textBytes.length);
    // First 4 bytes = 0x00000000 (text comment opcode), already zeros
    cellData.set(textBytes, 4);

    const dataBits = cellData.length * 8;
    const dataByteLen = cellData.length;

    // Cell descriptors
    const d1 = 0; // no refs, not exotic, level 0
    const d2 = dataByteLen * 2; // dataBits is exact multiple of 8, so no padding flag

    // Minimal BOC: magic + flags + cell
    const boc = new Uint8Array([
      0xb5, 0xee, 0x9c, 0x72, // BOC magic
      0x01,                     // flags: has_idx=0, has_crc32c=0, ref_size=1
      1,                        // offset_size=1
      1,                        // cells_count=1
      1,                        // roots_count=1
      0,                        // absent_count=0
      2 + dataByteLen,          // total_cells_size
      0,                        // root_index=0
      d1, d2,                   // cell descriptor
      ...cellData,              // cell data
    ]);

    // Base64 encode
    let binary = "";
    for (let i = 0; i < boc.length; i++) {
      binary += String.fromCharCode(boc[i]);
    }
    return btoa(binary);
  }
}

export const tonConnectManager = new TonConnectManager();
